import { AlertResponse, Subscription } from '@eagle/core-data-types';
import { Paper, styled, Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';
import { T_ONE } from '../../constants';
import { useResponsiveText, useSmallScreen } from '../../hooks';
import { useNotableEvents } from '../../hooks/use-notable-events';
import { getAlertTypeStyle, getEventLabelWithFlag, getFeatureLabelWithFlag, getFeatureType, innerText } from '../../util';
import { useBoolFlag } from '../flags';
import { ResultsDetailCard } from '../search-results-table';
import { SubscriptionUserRecipientDataType } from './subscriptions.types';
import { getEntityData, RecipientDisplay, SourceDisplay } from './subscriptions.util';

interface SubscriptionDetailsProps {
  disableLink?: boolean;
  displayOnCard: boolean;
  subscription: Subscription;
  subscriptionDetailPath: string;
  userRecipientDataType: SubscriptionUserRecipientDataType;
  'data-testid'?: string;
}

const StyledPaper = styled(Paper) <{ alertTypeId: string; featureTypeId: string; feature: string }>`
  ${({ alertTypeId, featureTypeId, feature }) => ({
    ...getAlertTypeStyle({ alertTypeId, featureTypeId, feature } as AlertResponse, useTheme(), useNotableEvents()),
    fontSize: '0.8rem',
    height: 'min-content',
    padding: '4px 12px',
    textAlign: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  })}
`;

export const SubscriptionDetails: FC<SubscriptionDetailsProps> = ({ displayOnCard, subscription, disableLink, subscriptionDetailPath, userRecipientDataType, ...props }): JSX.Element => {
  const href = useHref(subscriptionDetailPath);
  const smallScreen = useSmallScreen();
  const { alertFilters, recipient, _id: subscriptionId } = subscription;
  const { alertTypeId, feature } = alertFilters;
  const { t } = useTranslation(['admin', 'common']);

  const featureTypeId = getFeatureType(alertTypeId);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');

  const featureLabelText = t('common:terms.feature');
  const recipientLabelText = t('common:terms.recipient', { count: T_ONE });
  const fromLabelText = t('common:terms.from');
  const maxLabelLength = Math.max(
    innerText(featureLabelText).length,
    innerText(recipientLabelText).length,
    innerText(fromLabelText).length,
  );
  const captionStyles = useResponsiveText('0.75rem', maxLabelLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 15,
  });

  const renderContent = (): JSX.Element => <>
    <Typography
      variant="caption"
      style={{
        fontSize: captionStyles.fontSize,
        lineHeight: captionStyles.lineHeight,
        wordBreak: captionStyles.wordBreak,
        whiteSpace: captionStyles.whiteSpace,
        overflow: captionStyles.overflow,
        textOverflow: captionStyles.textOverflow,
      }}
    >
      {featureLabelText}
    </Typography>
    <Typography data-testid="subscription-feature">{feature && getFeatureLabelWithFlag(feature, flagUseEventV3)}</Typography>
    <Typography
      mt={1}
      variant="caption"
      style={{
        fontSize: captionStyles.fontSize,
        lineHeight: captionStyles.lineHeight,
        wordBreak: captionStyles.wordBreak,
        whiteSpace: captionStyles.whiteSpace,
        overflow: captionStyles.overflow,
        textOverflow: captionStyles.textOverflow,
      }}
    >
      {recipientLabelText}
    </Typography>
    <RecipientDisplay displaySubText={false} recipient={recipient} userDataType={userRecipientDataType} />
    <Typography
      mt={1}
      variant="caption"
      style={{
        fontSize: captionStyles.fontSize,
        lineHeight: captionStyles.lineHeight,
        wordBreak: captionStyles.wordBreak,
        whiteSpace: captionStyles.whiteSpace,
        overflow: captionStyles.overflow,
        textOverflow: captionStyles.textOverflow,
      }}
    >
      {fromLabelText}
    </Typography>
    <Box data-testid="subscription-entity">
      <SourceDisplay entityData={getEntityData(alertFilters)} />
    </Box>

    {alertTypeId && featureTypeId && feature
      && <Box mt={1} p={smallScreen ? 1 : '0.4rem'}>
        <StyledPaper
          data-testid="subscription-alert-description"
          elevation={0}
          alertTypeId={alertTypeId}
          featureTypeId={featureTypeId}
          feature={feature}
          sx={smallScreen ? { fontSize: '0.875rem', minHeight: 32, whiteSpace: 'normal', wordBreak: 'break-word' } : undefined}
        >
          {getEventLabelWithFlag(featureTypeId, alertTypeId, feature, flagUseEventV3)}
        </StyledPaper>
      </Box>
    }
  </>;

  return (
    <ResultsDetailCard
      content={renderContent()}
      href={disableLink ? undefined : `${href}/${subscriptionId}`}
      sx={{ p: displayOnCard ? 2 : 1 }}
      data-testid={props['data-testid']}
    />
  );
};
